import { useCallback, useEffect, useState } from 'react';
import { FormField, FormSelect, TextInput, Toggle } from '@/components/ui';
import { adminApi } from '@/shared/api/adminApi';
import { toast } from '@/components/ui/Toast';
import { ActionBindingsEditor, cleanActionBindings, type ActionBinding } from '../tagging/ActionBindingsEditor';

export interface Disposition {
  id: number;
  code: string;
  label: string;
  color?: string | null;
  actions?: ActionBinding[] | null;
  requires_followup?: boolean;
  lead_status?: string | null;
  requires_note?: boolean;
  alert_before_followup?: boolean;
  campaign_id?: number | null;
}

const LEAD_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: '— ללא שינוי —' },
  { value: 'completed', label: 'הושלם' },
  { value: 'queued', label: 'חזרה לתור' },
  { value: 'dnc', label: 'אל תחייג' },
  { value: 'not_relevant', label: 'לא רלוונטי' },
  { value: 'deal_closed', label: 'עסקה נסגרה' },
];

/**
 * Per-disposition rules for a campaign: for each effective code (global +
 * campaign-scoped), two toggles — requires_note + alert_before_followup. A
 * global code can't be edited in place; instead we offer a campaign-scoped copy
 * so the campaign gets its own override without affecting other campaigns.
 */
export function CampaignDispositions({ campaignId }: { campaignId: number }) {
  const [rows, setRows] = useState<Disposition[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    void adminApi.dispositions(campaignId)
      .then((res) => setRows((res.data as Disposition[]) ?? []))
      .catch(() => toast('שגיאה בטעינת קודי הסיום', 'danger'))
      .finally(() => setLoading(false));
  }, [campaignId]);

  useEffect(() => { load(); }, [load]);

  async function patchRule(d: Disposition, patch: { requires_note?: boolean; alert_before_followup?: boolean }) {
    setBusy(d.id);
    try {
      await adminApi.updateDisposition(d.id, patch);
      setRows((prev) => prev.map((r) => (r.id === d.id ? { ...r, ...patch } : r)));
      toast('הקוד עודכן', 'ok');
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast(msg ?? 'שגיאה בעדכון הקוד', 'danger');
    } finally {
      setBusy(null);
    }
  }

  // Clone a global code into a campaign-scoped one so it can carry per-campaign rules.
  async function cloneForCampaign(d: Disposition) {
    setBusy(d.id);
    try {
      await adminApi.createDisposition({
        code: d.code, label: d.label, color: d.color ?? undefined,
        requires_followup: d.requires_followup ?? false, lead_status: d.lead_status ?? undefined,
        requires_note: d.requires_note ?? false, alert_before_followup: d.alert_before_followup ?? false,
        campaign_id: campaignId,
      });
      toast('נוצר קוד ייעודי לקמפיין', 'ok');
      load();
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast(msg ?? 'שגיאה ביצירת קוד לקמפיין', 'danger');
    } finally {
      setBusy(null);
    }
  }

  if (loading) return <div style={{ color: 'var(--muted)' }}>טוען…</div>;

  return (
    <div className="stack">
      {rows.length === 0 ? (
        <div style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>אין קודי סיום.</div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {rows.map((d) => {
            const scoped = d.campaign_id === campaignId; // editable only when campaign-scoped
            return (
              <div key={d.id} style={{ padding: '10px 12px', border: '1px solid var(--line)', borderRadius: 'var(--r-2)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: 'var(--t-sm)' }}>
                    {d.label}
                    <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', fontWeight: 400 }}> · {scoped ? 'קוד קמפיין' : 'קוד גלובלי'}</span>
                  </strong>
                  {!scoped && (
                    <button type="button" className="btn" disabled={busy === d.id} onClick={() => cloneForCampaign(d)}>
                      צור קוד לקמפיין זה
                    </button>
                  )}
                </div>
                <div style={{ display: 'grid', gap: 6, marginTop: 8, opacity: scoped ? 1 : 0.55 }}>
                  <Toggle
                    checked={d.requires_note ?? false}
                    disabled={!scoped || busy === d.id}
                    onChange={(v) => patchRule(d, { requires_note: v })}
                    label="חובה לתעד הערה"
                  />
                  <Toggle
                    checked={d.alert_before_followup ?? false}
                    disabled={!scoped || busy === d.id}
                    onChange={(v) => patchRule(d, { alert_before_followup: v })}
                    label="התרע לנציג ~5 דק' לפני הפולו-אפ"
                  />
                </div>
                {scoped && (
                  <div style={{ marginTop: 8, borderTop: '1px solid var(--line)', paddingTop: 8 }}>
                    <RowActions disp={d} onSaved={load} />
                  </div>
                )}
                {!scoped && (
                  <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginTop: 6 }}>
                    כדי לקבוע כללים לקוד גלובלי בקמפיין זה בלבד — צור קוד ייעודי לקמפיין.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {adding ? (
        <AddDispositionForm campaignId={campaignId} onAdded={() => { setAdding(false); load(); }} onCancel={() => setAdding(false)} />
      ) : (
        <div><button type="button" className="btn" onClick={() => setAdding(true)}>+ קוד סיום לקמפיין</button></div>
      )}
    </div>
  );
}

/** עורך שיוך-פעולות מוטבע לקוד-סיום ייעודי-לקמפיין (עם שמירה מיידית). */
function RowActions({ disp, onSaved }: { disp: Disposition; onSaved: () => void }) {
  const [actions, setActions] = useState<ActionBinding[]>(disp.actions ?? []);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await adminApi.updateDisposition(disp.id, { actions: cleanActionBindings(actions) });
      toast('הפעולות נשמרו', 'ok');
      setDirty(false);
      onSaved();
    } catch { toast('שגיאה בשמירת הפעולות', 'danger'); }
    finally { setSaving(false); }
  }

  return (
    <div className="stack" style={{ gap: 8 }}>
      <div style={{ fontSize: 'var(--t-xs)', fontWeight: 600 }}>הפעל בבחירת הקוד</div>
      <ActionBindingsEditor value={actions} onChange={(a) => { setActions(a); setDirty(true); }} />
      {dirty && (
        <div><button type="button" className="btn primary" onClick={save} disabled={saving} style={{ height: 30 }}>{saving ? 'שומר…' : 'שמור פעולות'}</button></div>
      )}
    </div>
  );
}

function AddDispositionForm({ campaignId, onAdded, onCancel }: { campaignId: number; onAdded: () => void; onCancel: () => void }) {
  const [form, setForm] = useState({ code: '', label: '', lead_status: '', requires_note: false, alert_before_followup: false, actions: [] as ActionBinding[] });
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (!form.code.trim() || !form.label.trim()) { toast('יש למלא קוד ותווית', 'warn'); return; }
    setSaving(true);
    try {
      await adminApi.createDisposition({
        code: form.code.trim(), label: form.label.trim(), campaign_id: campaignId,
        lead_status: form.lead_status || undefined,
        requires_note: form.requires_note, alert_before_followup: form.alert_before_followup,
        actions: cleanActionBindings(form.actions),
      });
      toast('הקוד נוסף', 'ok');
      onAdded();
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast(msg ?? 'שגיאה בהוספת הקוד', 'danger');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-2)', padding: 12, display: 'grid', gap: 8 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
        <FormField label="קוד"><TextInput dir="ltr" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="callback" /></FormField>
        <FormField label="תווית"><TextInput value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} /></FormField>
        <FormField label="סטטוס ליד">
          <FormSelect value={form.lead_status} onChange={(e) => setForm({ ...form, lead_status: e.target.value })}>
            {LEAD_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </FormSelect>
        </FormField>
      </div>
      <Toggle checked={form.requires_note} onChange={(v) => setForm({ ...form, requires_note: v })} label="חובה לתעד הערה" />
      <Toggle checked={form.alert_before_followup} onChange={(v) => setForm({ ...form, alert_before_followup: v })} label="התרע לנציג ~5 דק' לפני הפולו-אפ" />
      <FormField label="הפעל בבחירת הקוד">
        <ActionBindingsEditor value={form.actions} onChange={(actions) => setForm({ ...form, actions })} />
      </FormField>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" className="btn" onClick={onCancel} disabled={saving}>ביטול</button>
        <button type="button" className="btn primary" onClick={submit} disabled={saving}>{saving ? 'שומר…' : 'הוסף'}</button>
      </div>
    </div>
  );
}
